import React, { useContext, useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { Menu, X, User, LogOut, Edit3, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

const Navbar = () => {
  const { user, login, logout, promptNickname } = useContext(AuthContext);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChangeNickname = () => {
    setUserMenuOpen(false);
    promptNickname();
  };

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-slate-200/10 bg-[#001f3f]/95 backdrop-blur supports-[backdrop-filter]:bg-[#001f3f]/80 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Podcast Branding */}
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3" data-testid="nav-logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_b421cab0-afec-4d5c-8996-440a4a24e0cf/artifacts/fy51jndf_IMG_4650.jpeg" 
              alt="SheLovesThee Logo" 
              className="h-9 sm:h-10 w-auto"
            />
            {/* SheLovesThee Podcast - Visible on mobile, hidden on desktop nav */}
            <span className="md:hidden font-bold text-white text-sm tracking-tight whitespace-nowrap" data-testid="mobile-podcast-title">
              SheLovesThee Podcast
            </span>
          </Link>

          <div className="hidden md:flex items-center space-x-1">
            <NavLink to="/" label="Home" />
            <NavLink to="/news" label="News" />
            <NavLink to="/jackson-state" label="Jackson State" />
            <NavLink to="/swac-pickem" label="SWAC Pick'em 🏈" highlight={true} />
            <NavLink to="/swac-scoreboard" label="Scoreboard" />
            <NavLink to="/qb-club" label="SWAC QB Club" />
            <NavLink to="/qb-analytics" label="QB Analytics" />
            <NavLink to="/fan-zone" label="Fan Zone" />
            <NavLink to="/about" label="About" />
            {user?.is_member && <NavLink to="/members" label="Members" />}
            {user && <NavLink to="/admin" label="Admin" />}
          </div>

          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 hover:bg-white/10 rounded-full px-3 py-1.5 transition-colors"
                  data-testid="user-menu-button"
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.nickname || user.name} className="h-8 w-8 rounded-full" />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-[#DC2626] flex items-center justify-center">
                      <User className="h-4 w-4 text-white" />
                    </div>
                  )}
                  <span className="text-sm font-medium">{user.nickname || user.name}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-slate-200 py-2 z-50" data-testid="user-dropdown">
                    <div className="px-4 py-2 border-b border-slate-100">
                      <p className="text-sm font-semibold text-slate-800">{user.nickname || user.name}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                    
                    <button
                      onClick={handleChangeNickname}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      data-testid="change-nickname-button"
                    >
                      <Edit3 className="h-4 w-4" />
                      <span>{user.nickname ? 'Change Nickname' : 'Set Nickname'}</span>
                    </button>
                    
                    <div className="border-t border-slate-100 mt-1 pt-1">
                      <button
                        onClick={() => { setUserMenuOpen(false); logout(); }}
                        className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        data-testid="logout-button"
                      >
                        <LogOut className="h-4 w-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Button 
                onClick={login} 
                className="bg-[#007AFF] text-white px-6 py-2 rounded-full font-bold uppercase tracking-wider hover:scale-105 transition-transform duration-200"
                data-testid="login-button"
              >
                Login
              </Button>
            )}
          </div>

          <button
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-2" data-testid="mobile-menu">
            <MobileNavLink to="/" label="Home" onClick={() => setMobileMenuOpen(false)} />
            <MobileNavLink to="/news" label="News" onClick={() => setMobileMenuOpen(false)} />
            <MobileNavLink to="/jackson-state" label="Jackson State" onClick={() => setMobileMenuOpen(false)} />
            <MobileNavLink to="/swac-pickem" label="SWAC Pick'em 🏈" onClick={() => setMobileMenuOpen(false)} highlight={true} />
            <MobileNavLink to="/swac-scoreboard" label="Scoreboard" onClick={() => setMobileMenuOpen(false)} />
            <MobileNavLink to="/qb-club" label="SWAC QB Club" onClick={() => setMobileMenuOpen(false)} />
            <MobileNavLink to="/qb-analytics" label="QB Analytics" onClick={() => setMobileMenuOpen(false)} />
            <MobileNavLink to="/fan-zone" label="Fan Zone" onClick={() => setMobileMenuOpen(false)} />
            <MobileNavLink to="/about" label="About" onClick={() => setMobileMenuOpen(false)} />
            {user?.is_member && <MobileNavLink to="/members" label="Members" onClick={() => setMobileMenuOpen(false)} />}
            {user && <MobileNavLink to="/admin" label="Admin" onClick={() => setMobileMenuOpen(false)} />}
            
            <div className="pt-4 border-t border-slate-700">
              {user ? (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 mb-2">
                    {user.picture ? (
                      <img src={user.picture} alt={user.nickname || user.name} className="h-8 w-8 rounded-full" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-[#DC2626] flex items-center justify-center">
                        <User className="h-4 w-4 text-white" />
                      </div>
                    )}
                    <div>
                      <span className="text-sm font-medium block">{user.nickname || user.name}</span>
                      <span className="text-xs text-slate-400">{user.email}</span>
                    </div>
                  </div>
                  <button 
                    onClick={() => { setMobileMenuOpen(false); promptNickname(); }} 
                    className="flex items-center space-x-2 text-sm text-slate-300 hover:text-[#007AFF] w-full py-1"
                    data-testid="mobile-change-nickname"
                  >
                    <Edit3 className="h-4 w-4" />
                    <span>{user.nickname ? 'Change Nickname' : 'Set Nickname'}</span>
                  </button>
                  <button 
                    onClick={() => { setMobileMenuOpen(false); logout(); }} 
                    className="flex items-center space-x-2 text-sm text-red-400 hover:text-red-300 w-full py-1"
                    data-testid="mobile-logout"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                  </button>
                </div>
              ) : (
                <Button onClick={login} className="w-full bg-[#007AFF] text-white">
                  Login
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const NavLink = ({ to, label, highlight = false }) => (
  <Link
    to={to}
    className={`text-sm font-bold uppercase tracking-widest transition-colors px-3 py-2 ${
      highlight 
        ? 'text-yellow-400 hover:text-yellow-300' 
        : 'text-slate-300 hover:text-[#007AFF]'
    }`}
    data-testid={`nav-link-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    {label}
  </Link>
);

const MobileNavLink = ({ to, label, onClick, highlight = false }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`block text-sm font-bold uppercase tracking-widest transition-colors px-3 py-2 ${
      highlight 
        ? 'text-yellow-400 hover:text-yellow-300' 
        : 'text-slate-300 hover:text-[#007AFF]'
    }`}
    data-testid={`mobile-nav-link-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    {label}
  </Link>
);

export default Navbar;